import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { settingsApi } from '../../api/settings';
import {
  PAID_ACTIONS,
  estimatePaidActionCostUsd,
  pricingFromCatalog,
  resolvePaidModelId,
} from '../../utils/estimatePaidActionCost';
import { formatEstimateUsd } from '../../utils/estimateOutlineCost';
import {
  REDRAFT,
  REDRAFT_CANCEL,
  REDRAFT_COST_FAILED,
  REDRAFT_IT,
  redraftCostLine,
} from '../../utils/writerCopy';
import '../Common/PaidActionConfirm.css';

/**
 * Cost check before one Note redraft. The figure comes from
 * estimatePaidActionCost. Cancel spends nothing. A missing estimate
 * does not start the call.
 */
export default function NoteRedraftConfirm({ onConfirm, onCancel }) {
  const action = PAID_ACTIONS.note_redraft;
  const [line, setLine] = useState('');
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [settingsRes, catalogRes] = await Promise.all([
          settingsApi.getSettings(),
          settingsApi.getOpenRouterCatalog().catch(() => null),
        ]);
        if (cancelled) return;
        const settings = settingsRes?.data || settingsRes || {};
        const preferences = settings?.model_preferences || {};
        const modelId = resolvePaidModelId(preferences, action);
        const models = catalogRes?.data?.models || catalogRes?.models || [];
        const pricing = pricingFromCatalog(models, modelId);
        const band = estimatePaidActionCostUsd(
          pricing,
          action,
          action.planningInputTokens,
        );
        const cost = band ? formatEstimateUsd(band.typical) : null;
        if (!cost) {
          setFailed(true);
          setLine(REDRAFT_COST_FAILED);
          return;
        }
        setFailed(false);
        setLine(redraftCostLine(cost));
      } catch {
        if (!cancelled) {
          setFailed(true);
          setLine(REDRAFT_COST_FAILED);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [action]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onCancel?.();
    };
    window.addEventListener('keydown', onKey);
    const id = window.requestAnimationFrame(() => setReady(true));
    return () => {
      window.removeEventListener('keydown', onKey);
      window.cancelAnimationFrame(id);
    };
  }, [onCancel]);

  const handleBackdrop = (event) => {
    if (event.target === event.currentTarget) onCancel?.();
  };

  return createPortal(
    <div
      className="paid-confirm-backdrop"
      onClick={ready ? handleBackdrop : undefined}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: ready ? 'auto' : 'none',
      }}
    >
      <div
        className="paid-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="note-redraft-title"
        style={{
          width: '100%',
          maxWidth: '28rem',
          padding: '24px',
          background: 'var(--surface)',
          color: 'var(--text-primary)',
          border: '1px solid var(--gilt-card-border)',
        }}
      >
        <h3 id="note-redraft-title" className="paid-confirm__title">{REDRAFT}</h3>
        <p className="paid-confirm__estimate" data-testid="redraft-cost">{line}</p>
        <div className="paid-confirm__actions">
          {!failed && (
            <button
              type="button"
              className="paid-confirm__btn"
              disabled={!line || sent}
              onClick={() => {
                if (sent) return;
                setSent(true);
                onConfirm?.();
              }}
            >
              {REDRAFT_IT}
            </button>
          )}
          <button
            type="button"
            className="paid-confirm__btn paid-confirm__btn--cancel"
            onClick={onCancel}
          >
            {REDRAFT_CANCEL}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
